import { todayKST, toDateString } from '../../../utils/date';
import { createAdditionalHotelRow, initialHotelRows } from './RevenueEventModal.hotelRows';

export { createAdditionalHotelRow };

export const EVENT_TYPE_OPTS = [
  { value: 'change', label: '변경' },
  { value: 'cancel', label: '취소' },
  { value: 'adjustment', label: '조정' },
  { value: 'booking', label: '예약' },
];

export const PAYMENT_METHOD_OPTS = [
  { value: 'cash', label: '현금' },
  { value: 'card', label: '카드' },
];

export const SERVICES = [
  { type: 'flight', label: '항공', target: '기존 항공 원가', baselineKey: 'flight_total' },
  { type: 'car', label: '렌트', target: '기존 렌트 원가', baselineKey: 'car_total' },
  { type: 'hotel', label: '호텔', target: '기존 호텔 원가', baselineKey: 'hotel_total' },
];

const MONEY_FIELDS = [
  'target_cost',
  'supplier_refund',
  'supplier_cancel_fee',
  'additional_cost',
  'customer_refund',
  'customer_additional_payment',
  'misc_amount',
];
export const FLIGHT_PART_GROUPS = {
  target: ['target_base_fare', 'target_fuel_surcharge', 'target_taxes'],
  refund: ['supplier_refund_base_fare', 'supplier_refund_fuel_surcharge', 'supplier_refund_taxes'],
  additional: ['additional_base_fare', 'additional_fuel_surcharge', 'additional_taxes'],
};
const FLIGHT_PART_FIELDS = Object.values(FLIGHT_PART_GROUPS).flat();
const SERVICE_TYPES = SERVICES.map((service) => service.type);

export const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
export const fmt = (value) => `${num(value).toLocaleString('ko-KR')}원`;
export const hasValue = (value) => value !== null && value !== undefined && value !== '';
export const formatAmountInput = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
export const parseAmountInput = (value) => {
  const text = String(value || '').replace(/,/g, '').split('.')[0];
  const sign = text.trim().startsWith('-') ? -1 : 1;
  const digits = text.replace(/[^\d]/g, '');
  if (digits === '' && sign < 0) return '-';
  return digits === '' ? null : sign * Number(digits);
};

export function emptyItem(type, itemLabel = null, sortOrder = 0) {
  return {
    item_type: type,
    item_label: itemLabel,
    sort_order: sortOrder,
    target_cost: null,
    supplier_refund: null,
    supplier_cancel_fee: null,
    additional_cost: null,
    customer_refund: null,
    customer_additional_payment: null,
    misc_amount: null,
    memo: null,
    target_base_fare: null,
    target_fuel_surcharge: null,
    target_taxes: null,
    supplier_refund_base_fare: null,
    supplier_refund_fuel_surcharge: null,
    supplier_refund_taxes: null,
    additional_base_fare: null,
    additional_fuel_surcharge: null,
    additional_taxes: null,
  };
}

export function flightPartTotal(item, group) {
  return (FLIGHT_PART_GROUPS[group] || []).reduce((sum, key) => sum + num(item?.[key]), 0);
}

function hasAnyFlightPart(item, group) {
  return (FLIGHT_PART_GROUPS[group] || []).some((key) => hasValue(item?.[key]));
}

function hydrateLegacyFlightParts(item) {
  if (item?.item_type !== 'flight') return item;
  const next = { ...item };
  if (!hasAnyFlightPart(next, 'target') && num(next.target_cost) !== 0) next.target_base_fare = num(next.target_cost);
  if (!hasAnyFlightPart(next, 'refund') && num(next.supplier_refund) !== 0) next.supplier_refund_base_fare = num(next.supplier_refund);
  if (!hasAnyFlightPart(next, 'additional') && num(next.additional_cost) !== 0) next.additional_base_fare = num(next.additional_cost);
  return {
    ...next,
    target_cost: hasAnyFlightPart(next, 'target') ? flightPartTotal(next, 'target') : next.target_cost,
    supplier_refund: hasAnyFlightPart(next, 'refund') ? flightPartTotal(next, 'refund') : next.supplier_refund,
    additional_cost: hasAnyFlightPart(next, 'additional') ? flightPartTotal(next, 'additional') : next.additional_cost,
  };
}

function cloneItem(item, fallbackType, sortOrder = 0) {
  const type = item?.item_type || fallbackType;
  const next = emptyItem(type, item?.item_label || null, hasValue(item?.sort_order) ? Number(item.sort_order) : sortOrder);
  for (const key of MONEY_FIELDS) {
    if (hasValue(item?.[key])) next[key] = num(item[key]);
  }
  for (const key of FLIGHT_PART_FIELDS) {
    if (hasValue(item?.[key])) next[key] = num(item[key]);
  }
  next.memo = item?.memo || null;
  return hydrateLegacyFlightParts(next);
}

const PASSENGER_LABELS = { adult: '성인', child: '소아', infant: '유아' };

function flightSourceLabel(flight, index) {
  const route = [flight?.departure_airport, flight?.arrival_airport].filter(Boolean).join('→');
  const parts = [
    `항공 #${index + 1}`,
    flight?.passenger_name,
    PASSENGER_LABELS[flight?.passenger_type],
    route,
    flight?.flight_number,
  ].filter(Boolean);
  return parts.join(' · ');
}

function flightSourceItem(flight, index) {
  const baseFare = num(flight?.base_fare);
  const fuelSurcharge = num(flight?.fuel_surcharge);
  const taxes = num(flight?.taxes);
  const partsTotal = baseFare + fuelSurcharge + taxes;
  const totalPayment = num(flight?.total_payment) || partsTotal;
  return {
    ...emptyItem('flight', flightSourceLabel(flight, index), index),
    target_cost: totalPayment,
    target_base_fare: baseFare || null,
    target_fuel_surcharge: fuelSurcharge || null,
    target_taxes: taxes || null,
    source_flight_id: flight?.id,
  };
}

function additionalFlightItem(index) {
  return {
    ...emptyItem('flight', null, index),
    is_additional: true,
  };
}

function mergeSavedFlightRows(baseRows, savedRows) {
  if (!savedRows.length) return baseRows;
  if (!baseRows.length) return savedRows;

  const mergeOne = (base, saved) => hydrateLegacyFlightParts({
    ...base,
    ...saved,
    item_label: base.item_label,
    sort_order: base.sort_order,
    target_cost: base.target_cost,
    target_base_fare: base.target_base_fare,
    target_fuel_surcharge: base.target_fuel_surcharge,
    target_taxes: base.target_taxes,
    source_flight_id: base.source_flight_id,
  });

  const savedByLabelIndex = new Map();
  const unmatchedSaved = [];
  for (const saved of savedRows) {
    const match = String(saved.item_label || '').match(/#\s*(\d+)/);
    const labelIndex = match ? Number(match[1]) - 1 : -1;
    if (labelIndex >= 0 && labelIndex < baseRows.length && !savedByLabelIndex.has(labelIndex)) {
      savedByLabelIndex.set(labelIndex, saved);
    } else {
      unmatchedSaved.push(saved);
    }
  }

  if (savedRows.length === 1) {
    return [
      mergeOne(baseRows[0], savedRows[0]),
      ...baseRows.slice(1),
    ];
  }

  if (savedByLabelIndex.size) {
    const nextRows = baseRows.map((base, index) => {
      const saved = savedByLabelIndex.get(index);
      return saved ? mergeOne(base, saved) : base;
    });
    let appendIndex = baseRows.length;
    for (const saved of unmatchedSaved) {
      nextRows.push({ ...saved, is_additional: true, sort_order: appendIndex });
      appendIndex += 1;
    }
    return nextRows;
  }

  const nextRows = baseRows.map((base, index) => {
    const saved = savedRows[index];
    if (!saved) return base;
    return mergeOne(base, saved);
  });

  for (let index = baseRows.length; index < savedRows.length; index += 1) {
    nextRows.push({ ...savedRows[index], is_additional: true, sort_order: index });
  }
  return nextRows;
}

function initialFlightRows(savedRows, baseline = {}, sourceFlights = []) {
  const sourceRows = (sourceFlights || []).map(flightSourceItem);
  if (sourceRows.length) return mergeSavedFlightRows(sourceRows, savedRows);
  if (savedRows.length) return savedRows;

  const baselineAmount = num(baseline.flight_total);
  return [{
    ...emptyItem('flight', '항공 #1', 0),
    target_cost: baselineAmount || null,
  }];
}

export function createAdditionalFlightRow(index) {
  return additionalFlightItem(index);
}

export function initialServiceRows(row, baseline = {}, sourceFlights = [], sourceHotels = []) {
  const rows = { flight: [], car: [], hotel: [] };
  if (row?.items?.length) {
    row.items.forEach((item, index) => {
      if (SERVICE_TYPES.includes(item.item_type)) rows[item.item_type].push(cloneItem(item, item.item_type, index));
    });
  } else if (row) {
    rows.flight.push(cloneItem({ item_type: 'flight', additional_cost: row.flight_cost }, 'flight', 0));
    rows.car.push(cloneItem({ item_type: 'car', additional_cost: row.car_cost }, 'car', 0));
    rows.hotel.push(cloneItem({ item_type: 'hotel', additional_cost: row.hotel_cost }, 'hotel', 0));
  }

  rows.flight = initialFlightRows(rows.flight, baseline, sourceFlights);
  rows.hotel = initialHotelRows(rows.hotel, baseline, sourceHotels);
  for (const service of SERVICES) {
    if (service.type === 'flight' || service.type === 'hotel') continue;
    if (!rows[service.type].length) rows[service.type].push(emptyItem(service.type, service.label, 0));
    const baselineAmount = num(baseline[service.baselineKey]);
    if (baselineAmount !== 0 && rows[service.type].every((item) => num(item.target_cost) === 0)) {
      rows[service.type][0] = { ...rows[service.type][0], target_cost: baselineAmount };
    }
  }
  return rows;
}

export function initialOtherItems(row, baseline = {}) {
  const customer = emptyItem('customer');
  const misc = emptyItem('misc');
  if (row?.items?.length) {
    row.items.forEach((item) => {
      if (item.item_type === 'customer') {
        for (const key of MONEY_FIELDS) customer[key] = num(customer[key]) + num(item[key]);
      }
      if (item.item_type === 'misc') misc.misc_amount = num(misc.misc_amount) + num(item.misc_amount);
    });
  } else if (row) {
    customer.customer_additional_payment = row.customer_amount;
    misc.misc_amount = row.misc;
  }
  const customerPaid = num(baseline.customer_paid);
  if (customerPaid !== 0 && num(customer.target_cost) === 0) customer.target_cost = customerPaid;
  return { customer, misc };
}

function legacyAdditionalPayment(row) {
  if (!row?.items?.length) return num(row?.customer_amount);
  return row.items
    .filter((item) => item.item_type === 'customer')
    .reduce((sum, item) => sum + num(item.customer_additional_payment), 0);
}

export function emptyPayment(eventDate, sortOrder = 0, amount = null) {
  return {
    sort_order: sortOrder,
    payment_date: toDateString(eventDate) || todayKST(),
    amount,
    payment_method: '',
    cash_receipt_issue_no: '',
    cash_receipt_amount: hasValue(amount) ? amount : null,
    card_deposit_memo: '',
    notes: '',
    card_deposit_status: 'pending',
  };
}

export function emptyRefund(eventDate, sortOrder = 0, amount = null) {
  return {
    sort_order: sortOrder,
    refund_date: toDateString(eventDate) || todayKST(),
    amount,
    refund_method: '',
    cash_receipt_cancel_approval_no: '',
    cash_receipt_cancel_amount: hasValue(amount) ? amount : null,
    refund_account: '',
    memo: '',
  };
}

export function initialPayments(row, defaultDate) {
  const savedPayments = (row?.payments || []).map((payment, index) => ({
    id: payment.id,
    sort_order: index,
    payment_date: toDateString(payment.payment_date),
    amount: hasValue(payment.amount) ? num(payment.amount) : null,
    payment_method: payment.payment_method || '',
    cash_receipt_issue_no: payment.cash_receipt_issue_no || '',
    cash_receipt_amount: hasValue(payment.cash_receipt_amount) ? num(payment.cash_receipt_amount) : null,
    card_deposit_memo: payment.card_deposit_memo || '',
    notes: payment.notes || '',
    card_deposit_status: payment.card_deposit_status || 'pending',
  }));
  if (savedPayments.length) return savedPayments;

  const legacyAmount = legacyAdditionalPayment(row);
  if (legacyAmount > 0) return [emptyPayment(row?.event_date, 0, legacyAmount)];
  return row ? [] : [emptyPayment(defaultDate, 0)];
}

export function initialRefunds(row, defaultDate) {
  const savedRefunds = (row?.refunds || []).map((refund, index) => ({
    id: refund.id,
    sort_order: index,
    refund_date: toDateString(refund.refund_date),
    amount: hasValue(refund.amount) ? num(refund.amount) : null,
    refund_method: refund.refund_method || '',
    cash_receipt_cancel_approval_no: refund.cash_receipt_cancel_approval_no || '',
    cash_receipt_cancel_amount: hasValue(refund.cash_receipt_cancel_amount) ? num(refund.cash_receipt_cancel_amount) : null,
    refund_account: refund.refund_account || '',
    memo: refund.memo || '',
  }));
  if (savedRefunds.length) return savedRefunds;

  const legacyRefund = row?.items?.length
    ? row.items
      .filter((item) => item.item_type === 'customer')
      .reduce((sum, item) => sum + num(item.customer_refund), 0)
    : 0;
  if (legacyRefund > 0) return [emptyRefund(row?.event_date, 0, legacyRefund)];
  return row ? [] : [];
}

export function cancelFee(item) {
  const target = num(item.target_cost);
  if (target === 0 || !hasValue(item.supplier_refund)) return 0;
  return target - num(item.supplier_refund);
}

export function supplierEffect(item) {
  return (hasValue(item.supplier_refund) ? num(item.supplier_refund) : 0) - num(item.additional_cost);
}

export function summarize(serviceRows, customer, misc) {
  let serviceRefund = 0;
  let additionalCost = 0;
  for (const service of SERVICES) {
    for (const item of serviceRows[service.type] || []) {
      serviceRefund += hasValue(item.supplier_refund) ? num(item.supplier_refund) : 0;
      additionalCost += num(item.additional_cost);
    }
  }
  const customerDelta = num(customer.customer_additional_payment) - num(customer.customer_refund);
  const miscAmount = num(misc.misc_amount);
  const deltaProfit = customerDelta + serviceRefund - additionalCost + miscAmount;
  return { customerDelta, serviceRefund, additionalCost, deltaProfit, amount: deltaProfit };
}

export function buildServicePayload(item, index) {
  const hasFlightRefundInput = item.item_type === 'flight' && FLIGHT_PART_GROUPS.refund.some((key) => hasValue(item[key]));
  const hasFlightAdditionalInput = item.item_type === 'flight' && FLIGHT_PART_GROUPS.additional.some((key) => hasValue(item[key]));
  const hasFlightPartInput = hasFlightRefundInput || hasFlightAdditionalInput;
  const hasInput = hasValue(item.supplier_refund)
    || num(item.additional_cost) !== 0
    || hasFlightPartInput
    || Boolean(item.memo);
  if (!hasInput) return { item_type: item.item_type };
  const payload = {
    ...item,
    sort_order: index,
    supplier_cancel_fee: hasValue(item.supplier_refund) ? cancelFee(item) : null,
  };
  if (payload.item_type === 'flight') {
    payload.target_cost = flightPartTotal(payload, 'target') || num(payload.target_cost);
    payload.supplier_refund = hasFlightRefundInput ? flightPartTotal(payload, 'refund') : null;
    payload.additional_cost = hasFlightAdditionalInput ? flightPartTotal(payload, 'additional') : null;
    payload.supplier_cancel_fee = hasFlightRefundInput ? cancelFee(payload) : null;
  }
  return payload;
}

export function buildCustomerPayload(item) {
  const hasInput = num(item.customer_refund) !== 0 || num(item.customer_additional_payment) !== 0;
  return !hasInput ? { item_type: 'customer' } : item;
}

export function paymentTotal(payments) {
  return payments.reduce((sum, payment) => sum + num(payment.amount), 0);
}

export function refundTotal(refunds) {
  return refunds.reduce((sum, refund) => sum + num(refund.amount), 0);
}
